import {
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollArea,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconDeviceFloppy,
  IconPlus,
  IconPrinter,
} from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import {
  deletePaymentDetails,
  getPaymentAndDetails,
  submitPayment,
} from "api/payment.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
  NOTIFICATION_TITLE_JOURNALS,
} from "common/constants";
import { navigateTo } from "common/navigation";
import {
  formatDate,
  formatNZD,
  getLocalDateString,
  safeNumber,
} from "common/utils";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import { FullScreenModal, SavingModal } from "pages/modals";
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInvoiceStore } from "stores/useInvoiceStore";
import { IDataEntryFormProps } from "types/interface.types";
import {
  JournalEntries,
  JournalEntry,
  PaymentDataEntryForm,
} from "types/model.types";
import { PaymentEntryRow } from "./AdminPaymentEntryRow";
import AdminPaymentPreview from "./AdminPaymentPreview";
import { PaymentHeaderRow } from "./DataEntryHeaderDefinitions";

const PaymentEntryForm: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = React.useState(false);
  const [initialData, setInitialData] = useState<PaymentDataEntryForm | null>(
    null,
  );
  const [isPrinted, setIsPrinted] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const { invoices: knownInvoices, loading: invoiceLoading } =
    useInvoiceStore();

  const [headerVersion, setHeaderVersion] = useState(0);
  const [invModalOpen, setInvModalOpen] = useState(false);
  const form = useForm<PaymentDataEntryForm>({
    initialValues: initialData ?? {
      id: 0,
      summary: {
        id: 0,
        createdOn: getLocalDateString(),
        expected: 0,
        entered: 0,
        coOpFees: 0,
        notes: "",
        isPrinted: false,
        isProcessed: false,
      },
      entries: [],
    },
  });
  const summary = form.getValues().summary;
  const [entries, handlers] = useListState<JournalEntry>([]);
  const numberInputRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleConfirmSelection(); // confirm selection 1st
        setInvModalOpen(false); // then close
      }
    };

    if (invModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [invModalOpen, selectedInvoiceIds]);

  // this removes state updates from layout-sensitve effects and removes layout feedback loop.
  // otherwise, we'll randomly get ResizeObserver loop error
  const { entered } = useMemo(() => {
    const total = entries.reduce(
      (sum, entry) => sum + safeNumber(entry.amount, 0),
      0,
    );

    const rounded = safeNumber(total);

    // const expectedTotal = entries.reduce(
    //   (sum, entry) => sum + safeNumber(entry.amount, 0),
    //   0,
    // );
    // const roundedExpected = safeNumber(expectedTotal); // * 100) / 100;

    return { entered: rounded };
  }, [entries]);

  useEffect(() => {
    form.setFieldValue("summary.entered", entered);
    //form.setFieldValue("summary.expected", expected);
  }, [entered]);

  // capture insert key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Insert") {
        e.preventDefault();
        handleAddEntry();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!recordId || (recordId ?? 0) === 0) return;
    let mounted = true;

    const fetchData = async () => {
      // non null assertion...
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      try {
        const response = await safeApiCall(getPaymentAndDetails(recordId));
        if (!mounted) return;
        setInitialData(response ?? null);
        setIsPrinted(response?.summary.isPrinted ?? false);
        setIsProcessed(response?.summary.isProcessed ?? false);
      } catch (error) {
        logError(error);
        notifications.show({
          title: "Error loading payment",
          message: "Could not fetch payment details.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    };
    fetchData();

    // what this protects
    // Can’t perform a React state update on an unmounted component.
    // if component is unmounted and we're setting state becuase of
    // api calls that's async etc...
    return () => {
      mounted = false;
    };
  }, [recordId]);

  useEffect(() => {
    if (!initialData) return;

    form.setValues({
      ...initialData,
      entries: [],
    });

    handlers.setState(
      initialData.entries?.map((e) => ({
        ...e,
        transientId: crypto.randomUUID(),
      })) ?? [],
    );
    setSelectedInvoiceIds(initialData.entries.map((e) => String(e.invoiceId)));
  }, [initialData]);

  const handleConfirmSelection = () => {
    const selectedIds = new Set(selectedInvoiceIds.map(Number));
    const existingMap = new Map(entries.map((e) => [e.invoiceId, e]));

    const combined: JournalEntries = [];
    for (const i of knownInvoices) {
      if (!selectedIds.has(i.invoiceId)) continue;

      const existing = existingMap.get(i.invoiceId);
      if (existing) {
        combined.push(existing); // preserve object identity
      } else {
        combined.push({
          transientId: crypto.randomUUID(),
          id: 0,
          invoiceId: i.invoiceId,
          debtorName: i.debtorName,
          amount: i.amount,
          coOpFees: null,
          parentId: i.parentId,
          createdOn: i.createdOn,
          debtorId: i.debtorId,
          originalAmount: i.originalAmount,
          effect: i.effect ?? 0,
        });
      }
    }
    const removedIds = entries
      .filter(
        (entry) =>
          !selectedIds.has(entry.invoiceId) && Number(entry.id ?? 0) > 0,
      )
      .map((entry) => Number(entry.id));

    startTransition(() => {
      handlers.setState(combined);
      handleRemoveEntries(removedIds);
      setInvModalOpen(false);
    });
  };

  const handleInvoiceChange = (index: number, updated: JournalEntry) => {
    if (summary.isProcessed) return;

    const match = knownInvoices.find(
      (inv) => inv.invoiceId === updated.invoiceId,
    );

    if (!match) {
      handlers.setItem(index, {
        ...entries[index],
        debtorName: "No invoice found",
      });
      return;
    }

    handlers.setItem(index, {
      ...entries[index],
      invoiceId: match.invoiceId,
      debtorId: match.debtorId,
      debtorName: match.debtorName,
      amount: updated.amount === 0 ? match.amount : updated.amount,
      createdOn: match.createdOn,
    });

    // Add to selectedInvoiceIds if not already present
    setSelectedInvoiceIds((prev) => {
      const idStr = String(updated.invoiceId);
      return prev.includes(idStr) ? prev : [...prev, idStr];
    });
  };

  const handleAddEntry = () => {
    handlers.append({
      transientId: crypto.randomUUID(),
      id: 0,
      invoiceId: 0,
      createdOn: getLocalDateString(),
      debtorId: 0,
      debtorName: "",
      amount: null,
      coOpFees: 0,
      originalAmount: 0,
      effect: 0,
    });
    const newIndex = form.values.entries.length - 1;
    requestAnimationFrame(() => {
      const wrapper = numberInputRefs.current[newIndex];
      const input = wrapper?.querySelector("input");
      input?.focus();
    });
  };

  const handleRemoveEntry = async (transientId: string) => {
    if (summary.isProcessed) return; // should never happen as the delete button is disabled when processed, but just in case.

    const entry = entries.find((e) => e.transientId === transientId);
    if (!entry) return;

    const entryId = Number(entry?.id);
    const invoiceId = String(entry.invoiceId); // ensure string for comparison

    // Remove from backend if persisted
    if (entryId > 0) {
      await handleRemoveEntries([entryId]);
    }

    // Remove locally
    handlers.setState((prev) =>
      prev.filter((e) => e.transientId !== transientId),
    );

    // remove from known invoices
    const removeFromSelected = () => {
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    };
    removeFromSelected();
  };

  const handleRemoveEntries = async (ids: number[]) => {
    if (ids.length === 0) return;
    const { summary } = form.getValues();
    if (summary.isProcessed) return; // should never happen as the delete buttons are disabled when processed, but just in case.

    setSaving(true);

    try {
      const response = await safeApiCall(
        deletePaymentDetails(Number(summary.id), ids),
      );
      if (response?.status === 200 && response.data?.success) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } catch (error) {
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    form.setFieldValue("summary.isPrinted", true);
    setIsPrinted(true);

    handleSubmit(false, false, true); // save before print preview
    const { summary } = form.getValues();

    setTimeout(() => {
      navigateTo(`/print/payment?id=${summary.id}&e=1`);
    }, 500);
  };

  const knownInvoiceIds = new Set(
    knownInvoices
      .map((inv) => inv?.invoiceId)
      .filter((id): id is number => typeof id === "number" && !isNaN(id)),
  );

  const seenInvoiceIds = new Set<number>();
  const invalidEntries = form.values.entries.filter((entry) => {
    const invoiceId = Number(entry.invoiceId);
    const isKnown = knownInvoiceIds.has(invoiceId);
    const isDuplicate = seenInvoiceIds.has(invoiceId);

    seenInvoiceIds.add(invoiceId);
    return !isKnown || isDuplicate;
  });

  const handleProcess = async () => {
    setTimeout(() => {
      setIsProcessed(true);
      handleSubmit(true, true);
    }, 0);
  };
  const canProceed = (): boolean => {
    const { summary } = form.getValues();
    return summary.expected === summary.entered && invalidEntries.length === 0;
  };
  const handleSubmit = async (
    close = false,
    process = false,
    suppressNotification = false,
  ) => {
    const formData = form.getValues();
    if (formData.summary.isProcessed) return; // already processed...

    if (canProceed() === false && process) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message:
          "Please check expected/entered match and/or invoice IDs are valid in each line item(s) and there are no duplicate invoices",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    } // should never happen as the buttons call the canProceed and disables control if form is invalid.

    setSaving(true);
    try {
      const payload: PaymentDataEntryForm = {
        ...formData,
        summary: {
          ...formData.summary,
          isProcessed: process,
        },
        entries,
      };

      const result: any = await safeApiCall(submitPayment(payload));
      // Merge backend response into form
      if (!result?.entries || !Array.isArray(result.entries)) {
        throw new Error(
          "Invalid response: entries is missing or not an array.",
        );
      }

      const updatedEntries = result.entries.map(
        (updatedEntry: JournalEntry) => {
          const existing = entries?.find(
            (e) => e.invoiceId === updatedEntry.invoiceId,
          );

          return {
            ...existing,
            ...updatedEntry, // this includes the new `id`
          };
        },
      );

      startTransition(() => {
        form.setValues({
          id: result.paymentId,
          summary: {
            ...form.values.summary,
            id: result.paymentId,
          },
          entries: [],
        });
        handlers.setState(updatedEntries);
      });

      if (!suppressNotification) {
        notifications.show({
          title: NOTIFICATION_TITLE_JOURNALS,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
      }

      (process || close) && onSaveComplete(close);
    } catch (error) {
      notifications.show({
        title: "Payment Entry",
        position: MantineNotificationPosition.TopCenter,
        message: `Failed to submit payment. Please try again. `,
        color: MantineNotificationColor.Error,
      });
      logError(error);
    } finally {
      setSaving(false);
    }
  };

  const [filter, setFilter] = useState("");

  const filteredInvoices = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return knownInvoices;

    const isNumeric = /^\d+$/.test(f);
    return knownInvoices.filter((inv) => {
      const name = inv.debtorName?.toLowerCase() ?? "";
      const parentName = inv.parentName?.toLowerCase() ?? "";

      if (isNumeric) {
        const invocieId = inv.invoiceId;
        const debtorId = inv.debtorId;
        const parentId = inv.parentId ?? undefined;
        const id = Number(f);

        return (
          debtorId === id || invocieId === id || (parentId && parentId === id)
        );
      }

      return name.includes(f) || parentName.includes(f);
    });
  }, [filter, knownInvoices]);

  const previousMonthInvoices = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return filteredInvoices.filter((inv) => {
      const d = new Date(inv.createdOn);
      const invMonth = d.getMonth();
      const invYear = d.getFullYear();

      // true if invoice is NOT in the current month
      return !(invMonth === currentMonth && invYear === currentYear);
    });
  }, [filteredInvoices]);

  const getResetAmount = (entry: JournalEntry) => {
    const invoice = knownInvoices.find(
      (inv) => inv.invoiceId === entry.invoiceId,
    );
    // Adjust this property name to whatever represents "amount still owing"
    const raw =
      invoice?.originalAmount ?? invoice?.amount ?? entry.originalAmount ?? 0;
    return safeNumber(raw, 0);
  };

  const handleApplyCoOpDeductionToAll = () => {
    const uniqueDebtors = new Set(entries.map((e) => e.debtorId));

    if (uniqueDebtors.size > 1) {
      // Mixed debtors → must have a single, valid common parent

      //const rawParentIds = entries.map((e) => e.parentId ?? null);
      const rawParentIds = entries.map((entry) => {
        const invoice = knownInvoices.find(
          (inv) => inv.invoiceId === entry.invoiceId,
        );
        // Adjust property name if your invoice model uses something else
        return invoice?.parentId ?? entry.parentId ?? null;
      });

      const hasMissingParent = rawParentIds.some((p) => p === null || p === 0);

      const nonNullParents = rawParentIds.filter((p) => p !== null && p !== 0);

      const uniqueParents = new Set(nonNullParents);

      // If ANY invoice has null/0/undefined parentId → invalid
      if (hasMissingParent || nonNullParents.length !== entries.length) {
        notifications.show({
          title: "Deductions",
          message:
            "You cannot apply deductions when some invoices have no parent account.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
        return;
      }

      // If parentIds differ → invalid
      if (uniqueParents.size > 1) {
        notifications.show({
          title: "Deductions",
          message:
            "You cannot apply deductions to mixed accounts without a common parent.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
        return;
      }

      // If we reach here → mixed debtors but same valid parent → OK
    }

    const restored: JournalEntries = entries.map((e) => ({
      ...e,
      amount: getResetAmount(e),
      originalAmount: getResetAmount(e),
      coOpFees: null,
    }));
    handlers.setState(restored);

    const totalApplied = restored.reduce(
      (sum, e) => sum + (e.coOpFees ?? 0),
      0,
    );
    if (totalApplied === summary.coOpFees) {
      notifications.show({
        title: "Deductions",
        message: "Deductions have already been fully applied.",
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    //let remaining = summary.coOpFees;
    let remaining = safeNumber(summary.coOpFees, 0);
    const totalExpectedPayment = restored.reduce(
      (sum, e) => sum + (e.originalAmount ?? 0),
      0,
    );
    if (remaining > totalExpectedPayment) {
      notifications.show({
        title: "Deductions",
        message:
          "Deduction exceeds the total expected payment. Expected invoice total: " +
          formatNZD(totalExpectedPayment),
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    // 1. Reset all coOpFees
    // let updated: JournalEntries = entries.map((e) => ({
    //   ...e,
    //   amount: getResetAmount(e),
    //   coOpFees: null,
    // }));

    // 2. Sort by largest originalAmount (or amount if that’s what you want)
    let updated = [...restored].sort(
      (a, b) => (b.amount ?? 0) - (a.amount ?? 0),
    );

    // 3. Apply deduction
    updated = updated.map((entry) => {
      if (remaining <= 0) return entry;

      const amt = safeNumber(entry.amount ?? 0, 0);

      if (amt > remaining) {
        // Invoice can absorb the remaining fee
        const newAmount = safeNumber(amt - remaining, 0);
        const appliedFee: number | null = safeNumber(remaining, 0);
        remaining = 0;

        return {
          ...entry,
          amount: newAmount,
          coOpFees: appliedFee,
        };
      } else {
        // Invoice is smaller than fee bucket → zero it out
        const appliedFee: number | null = safeNumber(amt, 0);
        remaining -= amt;

        return {
          ...entry,
          amount: 0,
          coOpFees: appliedFee,
        };
      }
    });

    // restore original order by id and transientId to maintain consistency in the UI
    updated.sort((a, b) => {
      const idA = Number(a.id);
      const idB = Number(b.id);

      const aIsNew = idA === 0;
      const bIsNew = idB === 0;

      // Case 1: both are new → sort by transientId
      if (aIsNew && bIsNew) {
        return a.transientId.localeCompare(b.transientId);
      }

      // Case 2: only A is new → A goes after B
      if (aIsNew) return 1;

      // Case 3: only B is new → B goes after A
      if (bIsNew) return -1;

      // Case 4: both have real IDs → sort by id ASC
      return idA - idB;
    });

    // 5. Commit to state
    handlers.setState(updated);
  };

  if (invoiceLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack>
      <Title order={3}>Payment Summary</Title>
      <Group grow align="flex-start">
        <TextInput
          label="Payment ID"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={form.values.summary.id === 0 ? "New" : form.values.summary.id}
          readOnly
        />
        <TextInput
          label="Created On"
          value={formatDate(form.values.summary.createdOn)}
          readOnly
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Deductions"
          {...form.getInputProps("summary.coOpFees")}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Expected Amount"
          {...form.getInputProps("summary.expected")}
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
            input: {
              backgroundColor:
                safeNumber(form.values.summary.expected ?? 0) !==
                safeNumber(form.values.summary.entered ?? 0)
                  ? "#fff9db" // light yellow
                  : undefined,
            },
          }}
        />
        <NumberInput
          label="Entered Amount"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={form.values.summary.entered}
          readOnly
        />
      </Group>
      <Textarea
        label="Notes"
        styles={{
          label: {
            fontWeight: 600, // makes label bold
          },
        }}
        {...form.getInputProps("summary.notes")}
        mt="md"
      />
      <Divider
        my="md"
        label={`By default, invoice search only spans last 6 months. This will not work
        if the invoice is older than 6 months. ${knownInvoices.length} invoices were loaded.`}
      />
      <Title order={3}>Payment Entries</Title>
      <Group justify="flex-start" mt="md">
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={handleAddEntry}
          text="Add Invoice"
          variant="light"
        />
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={() => setInvModalOpen(true)}
          text="Select Invoice(s)"
          variant="light"
        />

        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={handleApplyCoOpDeductionToAll}
          text="Reset/Apply Deductions"
          variant="gradient"
        />
      </Group>
      <Stack gap="sm" className="form">
        <PaymentHeaderRow key={headerVersion} />
        {entries.map((entry, index) => (
          <PaymentEntryRow
            key={`${entry.transientId}-${index}`}
            entry={entry}
            index={index}
            onUpdate={(updated) => {
              handleInvoiceChange(index, updated);
            }}
            onRemove={() => handleRemoveEntry(entry.transientId)}
          ></PaymentEntryRow>
        ))}
      </Stack>

      <Group justify="space-between" mt="md">
        <Button
          disabled={summary.isProcessed}
          variant="light"
          onClick={handleAddEntry}
          leftSection={<IconPlus size={16} />}
        >
          Add Invoice
        </Button>

        <Group>
          <Button
            onClick={() => handleSubmit()}
            disabled={saving || summary.isProcessed}
            title="Save Payment. This does not process the payment."
            leftSection={<IconDeviceFloppy size={16} />}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            disabled={saving || summary.isProcessed}
            title="Save Payment. This does not process the payment."
            leftSection={<IconDeviceFloppy size={16} />}
          >
            Save & Close
          </Button>
          <Button
            onClick={handlePrint}
            disabled={
              summary.id === 0 || summary.expected !== summary.entered || saving
            }
            leftSection={<IconPrinter size={16} />}
          >
            Print
          </Button>
          <Button
            title="Process the payment. This action cannot be undone."
            onClick={handleProcess}
            disabled={
              entries.length === 0 ||
              saving ||
              isProcessed ||
              isPrinted === false ||
              summary.expected !== summary.entered
            }
            rightSection={
              <IconCheck
                color={`${isProcessed ? "green" : "gray"}`}
                size={16}
              />
            }
          >
            Process
          </Button>
        </Group>
      </Group>
      {/* Invoice selection */}
      <Modal
        size={"xl"}
        opened={invModalOpen}
        onClose={() => {
          setInvModalOpen(false);
          handleConfirmSelection();
        }}
        zIndex={3000}
        overlayProps={{ opacity: 0.3, blur: 2 }}
        title="Select Invoice(s) to Record Payment Against"
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <ScrollArea>
            {/* 🔍 Filter box */}
            <TextInput
              placeholder="Filter by debtor name or invoice #"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              mb="md"
            />
            {/* Master checkbox */}
            <Checkbox
              mb="sm"
              label="Select / Unselect All"
              checked={
                filteredInvoices.length > 0 &&
                selectedInvoiceIds.length === filteredInvoices.length
              }
              indeterminate={
                selectedInvoiceIds.length > 0 &&
                selectedInvoiceIds.length < filteredInvoices.length
              }
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  setSelectedInvoiceIds(
                    filteredInvoices.map((i) => String(i.invoiceId)),
                  );
                } else {
                  setSelectedInvoiceIds([]);
                }
              }}
            />
            <Checkbox
              mb="sm"
              label="Select All (Previous Months Only)"
              checked={
                previousMonthInvoices.length > 0 &&
                previousMonthInvoices.every((inv) =>
                  selectedInvoiceIds.includes(String(inv.invoiceId)),
                )
              }
              indeterminate={
                previousMonthInvoices.some((inv) =>
                  selectedInvoiceIds.includes(String(inv.invoiceId)),
                ) &&
                !previousMonthInvoices.every((inv) =>
                  selectedInvoiceIds.includes(String(inv.invoiceId)),
                )
              }
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  // merge previous-month IDs into selection
                  const ids = previousMonthInvoices.map((i) =>
                    String(i.invoiceId),
                  );
                  setSelectedInvoiceIds((prev) =>
                    Array.from(new Set([...prev, ...ids])),
                  );
                } else {
                  // remove previous-month IDs from selection
                  const ids = new Set(
                    previousMonthInvoices.map((i) => String(i.invoiceId)),
                  );
                  setSelectedInvoiceIds((prev) =>
                    prev.filter((id) => !ids.has(id)),
                  );
                }
              }}
            />

            <Checkbox.Group
              value={selectedInvoiceIds}
              onChange={setSelectedInvoiceIds}
            >
              <Stack gap="sm">
                {filteredInvoices.map((invoice, index) => (
                  <Checkbox
                    key={`${index}-${invoice.transientId}`}
                    value={String(invoice.invoiceId)}
                    label={`Debtor: ${invoice.debtorName}, INV #: ${invoice.invoiceId} on ${formatDate(invoice.createdOn)}, Amount: ${formatNZD(invoice.amount ?? 0)}`}
                  />
                ))}
              </Stack>
            </Checkbox.Group>

            <DefaultButtonAdd
              style={{ marginTop: "8px" }}
              text={"Add to Entry"}
              onClick={handleConfirmSelection}
            />
          </ScrollArea>
        </div>
      </Modal>
      <SavingModal opened={saving} onClose={() => setSaving(false)} />
      {/* print preview */}
      <FullScreenModal opened={preview} onClose={() => setPreview(false)}>
        {recordId && <AdminPaymentPreview id={recordId} />}
      </FullScreenModal>
    </Stack>
  );
};

export default PaymentEntryForm;
